import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { setStoredViewMode } from '@/hooks/useViewMode';
import { toast } from 'sonner';

const SPORTS = [
  { key: 'football', label: '🏈 Football' },
  { key: 'soccer', label: '⚽ Soccer' },
  { key: 'basketball', label: '🏀 Basketball' },
  { key: 'baseball', label: '⚾ Baseball' },
];

/**
 * Lets an existing player upgrade their account into a coach account by
 * creating a coach_profiles row tied to their user. After activation the
 * user can flip between Player and Coach mode using ModeSwitcher.
 */
export function ActivateCoachCard() {
  const { user } = useAuth();
  const [isCoach, setIsCoach] = useState<boolean | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [org, setOrg] = useState('');
  const [sport, setSport] = useState('football');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('coach_profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => setIsCoach(!!data));
  }, [user]);

  if (!user || isCoach === null || isCoach) return null;

  const activate = async () => {
    if (!user) return;
    setSubmitting(true);
    const { error } = await supabase
      .from('coach_profiles')
      .insert({
        user_id: user.id,
        organization_name: org.trim() || null,
        primary_sport: sport,
      });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || 'Could not activate coach mode');
      return;
    }
    toast.success('Coach mode activated!');
    setStoredViewMode('coach');
    // Reload so the router picks up the new coach view.
    setTimeout(() => window.location.reload(), 400);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.13 }}
      className="bg-card rounded-2xl border-2 border-primary/40 p-5 shadow-card space-y-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <GraduationCap className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            Become a Coach
            <Sparkles className="w-4 h-4 text-primary" />
          </h3>
          <p className="text-xs text-muted-foreground">
            Add a Coach side to your account — create teams, assign lessons, and track player progress. You can switch between Player and Coach mode any time.
          </p>
        </div>
      </div>

      {!expanded ? (
        <Button
          onClick={() => setExpanded(true)}
          className="w-full h-11 font-display font-bold rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          Activate Coach Mode
        </Button>
      ) : (
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <label className="text-xs font-display font-bold text-muted-foreground uppercase tracking-wide">
              Organization (optional)
            </label>
            <Input
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              placeholder="e.g. Riverside High Football"
              maxLength={80}
              className="h-11 rounded-xl"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-display font-bold text-muted-foreground uppercase tracking-wide">
              Primary sport
            </label>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              className="w-full h-11 rounded-xl border-2 border-input bg-background px-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {SPORTS.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setExpanded(false)}
              disabled={submitting}
              className="flex-1 h-11 rounded-xl font-display font-bold"
            >
              Cancel
            </Button>
            <Button
              onClick={activate}
              disabled={submitting}
              className="flex-1 h-11 rounded-xl font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {submitting ? 'Activating…' : 'Activate'}
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
